import type {EmailAutomationApiItem} from "./types";
import type {CreateAutomationFormValues} from "./schema";

export const MAX_EMAIL_AUTOMATIONS_PER_TRIGGER = 5;

export const EMAIL_AUTOMATION_TRIGGER_LIMIT_REACHED_MESSAGE =
  "You've reached the limit of 5 email automations for this trigger. Delete an existing automation before adding another.";

export const EMAIL_AUTOMATIONS_ALL_TRIGGERS_AT_LIMIT_MESSAGE =
  "You've reached 5 automations for both After purchase and Abandoned purchase. Delete one before creating another.";

export const EMAIL_AUTOMATION_AFTER_PURCHASE_AT_LIMIT_HELPER =
  "You've reached the limit of 5 email automations for After purchase. Delete an existing automation to use this trigger.";

export const EMAIL_AUTOMATION_ABANDONED_PURCHASE_AT_LIMIT_HELPER =
  "You've reached the limit of 5 email automations for Abandoned purchase. Delete an existing automation to use this trigger.";

/** Shown under the trigger field when create options are grayed out at the account cap. */
export function helperTextForTriggerQuotaBlocked(
  blocked?: Partial<Record<"after_purchase" | "abandoned_purchase", boolean>>
): string | undefined {
  if (!blocked?.after_purchase && !blocked?.abandoned_purchase) return undefined;
  if (blocked.after_purchase && blocked.abandoned_purchase) {
    return EMAIL_AUTOMATIONS_ALL_TRIGGERS_AT_LIMIT_MESSAGE;
  }
  if (blocked.after_purchase) return EMAIL_AUTOMATION_AFTER_PURCHASE_AT_LIMIT_HELPER;
  return EMAIL_AUTOMATION_ABANDONED_PURCHASE_AT_LIMIT_HELPER;
}

export type EmailAutomationApiTrigger = EmailAutomationApiItem["trigger"];

export function countAutomationsWithApiTrigger(
  items: EmailAutomationApiItem[],
  apiTrigger: EmailAutomationApiTrigger
): number {
  return items.filter((i) => i.trigger === apiTrigger).length;
}

export function isApiTriggerAtOrOverLimit(
  items: EmailAutomationApiItem[],
  apiTrigger: EmailAutomationApiTrigger
): boolean {
  return (
    countAutomationsWithApiTrigger(items, apiTrigger) >= MAX_EMAIL_AUTOMATIONS_PER_TRIGGER
  );
}

/** Per–form-trigger disabled flags for the create drawer Select. */
export function triggerQuotaBlockedFromCatalog(items: EmailAutomationApiItem[]): {
  after_purchase: boolean;
  abandoned_purchase: boolean;
} {
  return {
    after_purchase: isApiTriggerAtOrOverLimit(items, "after-purchase"),
    abandoned_purchase: isApiTriggerAtOrOverLimit(items, "abandoned-purchase"),
  };
}

export function pickDefaultTriggerForCreate(blocked: {
  after_purchase: boolean;
  abandoned_purchase: boolean;
}): CreateAutomationFormValues["trigger"] {
  if (!blocked.after_purchase) return "after_purchase";
  if (!blocked.abandoned_purchase) return "abandoned_purchase";
  return "after_purchase";
}

export function formTriggerToApiTrigger(
  formTrigger: CreateAutomationFormValues["trigger"]
): EmailAutomationApiTrigger {
  return formTrigger === "after_purchase" ? "after-purchase" : "abandoned-purchase";
}

export function countForFormTriggerFromSplitLists(
  formTrigger: CreateAutomationFormValues["trigger"],
  afterPurchaseItems: EmailAutomationApiItem[],
  abandonedPurchaseItems: EmailAutomationApiItem[]
): number {
  return formTrigger === "after_purchase"
    ? afterPurchaseItems.length
    : abandonedPurchaseItems.length;
}
